import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useReviewWorkspaceRole } from 'hooks/useWorkspace';

import { usePrototypePreview } from 'Pages/TeamDashboard/CreateReview/components/PrototypePreviewContext';

import { getReviewDataAction, setDesignReviewHistory } from 'Redux/Actions/designReviewActions';
import { setCurrentRecord } from 'Redux/Actions/historyActions';

import { trackEvent } from 'services/eventTracking';
import {
  dismissVariant,
  getDesignReviewPostProcess,
  refineDesignRemix,
  triggerPageRemix,
} from 'services/teamDashboardReviews';

import { DESIGN_REMIX_POLL_INTERVAL, DESIGN_REMIX_STATUS } from 'utlis/constants';
import {
  buildUniquePanelKey,
  dedupeRefinements,
  findOrmRemixPreview,
  normalizeRemixPreview,
  upsertOrmRemixPreview,
} from 'utlis/remixVariants';
import { errorReactToastify } from 'utlis/toasts';

const DEFAULT_REFINEMENT_PROMPT = 'Generate another variant of this page with the same feedback but'
+ ' a different approach. A variant would help explore the recommendations further and spark new ideas.';

export const PageVariantPanel = ({
  page,
  eligibleFeedback,
  designReviewUuid,
  isPublicView = false,
}) => {
  const dispatch = useDispatch();
  const store = useStore();
  const { registerPrototype, clearPrototype } = usePrototypePreview();

  const pageKey = `page_${page}`;

  const currentRecord = useSelector(state => state.historyReducer.currentRecord);
  const reviewCount = useSelector(state => state.designReview.reviewCount);
  const hasCredits = (reviewCount?.reviews_remaining ?? 0) > 0;
  const userData = useSelector(state => state.userStore);
  const userProfile = useSelector(state => state.userProfile);
  const { isReviewOwner: isWorkspaceOwner, isReviewEditor, isReviewViewer } = useReviewWorkspaceRole(currentRecord);
  const isCreator = Boolean(userData?.uuid && currentRecord?.owner?.uuid === userData.uuid);

  // Workspace owners/editors can generate on any audit in the workspace;
  // otherwise fall back to the audit's own creator
  const isReviewOwner = !isReviewViewer && (isWorkspaceOwner || isReviewEditor || isCreator);

  // only let the audit creator dismiss, and only while they are still a workspace owner/editor
  const canDismissVariants = isCreator && (isWorkspaceOwner || isReviewEditor);

  const [remixStatus, setRemixStatus] = useState(null);
  const [remixProcessUuid, setRemixProcessUuid] = useState(null);
  const [remixPreview, setRemixPreview] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isRefining, setIsRefining] = useState(false);
  const [refiningProcessUuid, setRefiningProcessUuid] = useState(null);
  const [remixError, setRemixError] = useState('');
  const [isTriggeringRemix, setIsTriggeringRemix] = useState(false);
  const [isFetchingImage, setIsFetchingImage] = useState(false);
  // imageError is set inside ensureDesignImageUrl for telemetry / debounce purposes
  // but is no longer rendered — failures surface via remixError → carousel failed-CTA.
  // eslint-disable-next-line no-unused-vars
  const [imageError, setImageError] = useState('');
  const [isDismissingUuid, setIsDismissingUuid] = useState(null);

  const remixPreviewRef = useRef(null);
  const isTriggeringRef = useRef(false);
  const isRefiningRef = useRef(false);
  const isMountedRef = useRef(true);

  // Always points at the latest preview so async handlers never act on a stale snapshot
  remixPreviewRef.current = remixPreview;
  isRefiningRef.current = isRefining;

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const isPageInRange = useMemo(() => {
    const pageNumber = Number(page);
    const frameImages = currentRecord?.extra_data?.frame_images;
    const totalPages = Array.isArray(frameImages) ? frameImages.length : 0;

    if (!Number.isFinite(pageNumber) || pageNumber < 1) {
      return false;
    }

    if (totalPages > 0) {
      return pageNumber <= totalPages;
    }

    return false;
  }, [page, currentRecord]);

  const hasEligibleFeedback = Array.isArray(eligibleFeedback) && eligibleFeedback.length > 0;

  const inspirations = useMemo(() => {
    if (!remixPreview) {
      return [];
    }
    const original = { ...remixPreview, version: 1, isOriginal: true };
    const refinements = Array.isArray(remixPreview.refinements)
      ? dedupeRefinements(remixPreview.refinements)
        .filter(r => r.version >= 2)
        .sort((a, b) => a.version - b.version)
      : [];

    return [original, ...refinements];
  }, [remixPreview]);

  const safeIndex = Math.min(activeIndex, Math.max(inspirations.length - 1, 0));

  useEffect(() => {
    if (safeIndex !== activeIndex) {
      setActiveIndex(safeIndex);
    }
  }, [safeIndex, activeIndex]);

  const activeInspiration = inspirations[safeIndex] || null;
  const activeUploadKey = activeInspiration?.upload_info?.key;
  const activePreviewSrc = activeUploadKey
    ? `${process.env.REACT_APP_PREVIEW_CLOUDFRONT_URL}${activeUploadKey}`
    : null;
  const activeHtmlUploadKey = activeInspiration?.html_upload_info?.key;
  const activePreviewHtmlUrl = activeHtmlUploadKey
    ? `${process.env.REACT_APP_PREVIEW_CLOUDFRONT_URL}${activeHtmlUploadKey}`
    : null;
  const activeProcessUuid = activeInspiration?.process_uuid || null;

  const isRemixIdle = remixStatus === null || remixStatus === undefined;
  const isRemixInProgress = remixStatus === DESIGN_REMIX_STATUS.IN_PROGRESS;

  const ensureDesignImageUrl = useCallback(async () => {
    const pageIndex = page - 1;
    const framePath = currentRecord?.extra_data?.frame_images?.[pageIndex]
      || currentRecord?.extra_data?.frame_image;

    setIsFetchingImage(true);
    setImageError('');

    try {
      if (!framePath) {
        throw new Error('Missing design image path');
      }

      const prefixUrl = process.env.REACT_APP_FIREBASE_STORAGE_PREFIX;
      const postfixUrl = '?alt=media';
      const imageUrl = `${prefixUrl}${encodeURIComponent(framePath)}${postfixUrl}`;

      return imageUrl;
    }
    catch (error) {
      trackEvent('[error] page variant image download failed', {
        recordUuid: currentRecord?.uuid,
        pageKey,
        message: error?.message,
      });

      setImageError('Unable to fetch design image URL');

      return null;
    }
    finally {
      setIsFetchingImage(false);
    }
  }, [currentRecord, page, pageKey]);

  const updateRecordRemix = useCallback((payload = {}) => {
    // Read the latest store state: callbacks can be stale when several writers update the record
    const latestState = store.getState();
    const latestRecord = latestState.historyReducer.currentRecord;
    const latestHistory = latestState.designReview.designReviewHistory;

    if (!latestRecord) {
      return;
    }

    const extraData = latestRecord.extra_data || {};
    const postProcesses = extraData.post_processes || {};
    const appliedPreview = postProcesses.applied_feedback_preview || {};

    const existingAppliedEntry = appliedPreview[pageKey] || {};
    const payloadRemixPreview = payload.remix_preview || {};

    const mergedAppliedEntry = {
      ...existingAppliedEntry,
      ...payload,
      remix_preview: {
        ...(existingAppliedEntry.remix_preview || {}),
        ...payloadRemixPreview,
        status: payloadRemixPreview.status
          ?? payload.status
          ?? existingAppliedEntry.remix_preview?.status
          ?? existingAppliedEntry.status,
        process_uuid: payloadRemixPreview.process_uuid
          ?? payload.process_uuid
          ?? existingAppliedEntry.remix_preview?.process_uuid
          ?? existingAppliedEntry.process_uuid,
        error_message: payloadRemixPreview.error_message
          ?? payload.error_message
          ?? existingAppliedEntry.remix_preview?.error_message
          ?? existingAppliedEntry.error_message,
        upload_info: payloadRemixPreview.upload_info
          ?? payload.upload_info
          ?? existingAppliedEntry.remix_preview?.upload_info
          ?? existingAppliedEntry.upload_info,
        html_upload_info: payloadRemixPreview.html_upload_info
          ?? payload.html_upload_info
          ?? existingAppliedEntry.remix_preview?.html_upload_info
          ?? existingAppliedEntry.html_upload_info,
        generated_at: payloadRemixPreview.generated_at
          ?? payload.generated_at
          ?? existingAppliedEntry.remix_preview?.generated_at
          ?? existingAppliedEntry.generated_at,
      },
    };

    const updatedAppliedPreview = {
      ...appliedPreview,
      [pageKey]: mergedAppliedEntry,
    };

    // Start from what the sync effect currently reads, so a partial payload never replaces the whole preview
    const baseOrmPreview = findOrmRemixPreview(latestRecord, pageKey)
      || existingAppliedEntry.remix_preview
      || existingAppliedEntry;

    const mergedOrmPreview = {
      ...baseOrmPreview,
      ...payloadRemixPreview,
      status: payloadRemixPreview.status
        ?? payload.status
        ?? baseOrmPreview.status,
      process_uuid: payloadRemixPreview.process_uuid
        ?? payload.process_uuid
        ?? baseOrmPreview.process_uuid,
      error_message: payloadRemixPreview.error_message
        ?? payload.error_message
        ?? baseOrmPreview.error_message,
      upload_info: payloadRemixPreview.upload_info
        ?? payload.upload_info
        ?? baseOrmPreview.upload_info,
      html_upload_info: payloadRemixPreview.html_upload_info
        ?? payload.html_upload_info
        ?? baseOrmPreview.html_upload_info,
      generated_at: payloadRemixPreview.generated_at
        ?? payload.generated_at
        ?? baseOrmPreview.generated_at,
    };

    const updatedRecord = {
      ...latestRecord,
      orm_post_processes: upsertOrmRemixPreview(
        latestRecord.orm_post_processes,
        pageKey,
        mergedOrmPreview,
        { page }
      ),
      extra_data: {
        ...extraData,
        post_processes: {
          ...postProcesses,
          applied_feedback_preview: updatedAppliedPreview,
        },
      },
    };

    dispatch(setCurrentRecord(updatedRecord));

    if (Array.isArray(latestHistory?.results)) {
      const historyIndex = latestHistory.results.findIndex(
        record => String(record?.uuid) === String(latestRecord?.uuid)
      );

      if (historyIndex > -1) {
        const updatedHistory = {
          ...latestHistory,
          results: [...latestHistory.results],
        };

        updatedHistory.results[historyIndex] = {
          ...updatedHistory.results[historyIndex],
          ...updatedRecord,
        };

        dispatch(setDesignReviewHistory(updatedHistory));
      }
    }
  }, [dispatch, page, pageKey, store]);

  useEffect(() => {
    const previewBucket = currentRecord?.extra_data?.post_processes?.applied_feedback_preview || {};
    const existingEntry = previewBucket[pageKey] || null;
    // Row-backed state wins; extra_data covers reviews/endpoints without orm_post_processes
    const pageRemixPreview = findOrmRemixPreview(currentRecord, pageKey)
      || existingEntry?.remix_preview
      || existingEntry
      || null;
    const resolvedStatus = pageRemixPreview?.status ?? existingEntry?.status;
    const resolvedProcessUuid = pageRemixPreview?.process_uuid ?? existingEntry?.process_uuid;

    if (pageRemixPreview && (resolvedStatus || resolvedProcessUuid)) {
      setRemixStatus(resolvedStatus ?? DESIGN_REMIX_STATUS.COMPLETED);
      setRemixProcessUuid(resolvedProcessUuid || null);
      setRemixPreview(normalizeRemixPreview(pageRemixPreview));
      setRemixError(pageRemixPreview.error_message || existingEntry?.error_message || '');
    }
    else {
      setRemixStatus(null);
      setRemixProcessUuid(null);
      setRemixPreview(null);
      setRemixError('');
    }
  }, [currentRecord, pageKey]);

  useEffect(() => {
    if (
      !remixProcessUuid
      || remixStatus !== DESIGN_REMIX_STATUS.IN_PROGRESS
      || isPublicView
    ) {
      return undefined;
    }

    let cancelled = false;
    let intervalId = null;

    const stopPolling = () => {
      cancelled = true;

      if (intervalId) {
        clearInterval(intervalId);
      }
    };

    const checkStatus = async () => {
      try {
        const postProcess = await getDesignReviewPostProcess(remixProcessUuid);

        if (!postProcess || cancelled) {
          return;
        }

        if (postProcess.status !== undefined) {
          setRemixStatus(postProcess.status);
        }

        if (postProcess.status === DESIGN_REMIX_STATUS.COMPLETED) {
          const resultData = postProcess.result_data || {};

          setRemixPreview(resultData);
          setRemixError('');

          updateRecordRemix({
            status: DESIGN_REMIX_STATUS.COMPLETED,
            process_uuid: postProcess.uuid,
            upload_info: resultData.upload_info,
            html_upload_info: resultData.html_upload_info,
            generated_at: resultData.generated_at,
            remix_preview: {
              status: DESIGN_REMIX_STATUS.COMPLETED,
              upload_info: resultData.upload_info,
              html_upload_info: resultData.html_upload_info,
              generated_at: resultData.generated_at,
              process_uuid: postProcess.uuid,
            },
          });

          dispatch(getReviewDataAction());
          stopPolling();
        }
        else if (postProcess.status === DESIGN_REMIX_STATUS.FAILED) {
          const errorMessage = postProcess.error_message || 'Page variant failed';

          setRemixError(errorMessage);

          updateRecordRemix({
            status: DESIGN_REMIX_STATUS.FAILED,
            process_uuid: postProcess.uuid,
            error_message: errorMessage,
            remix_preview: {
              status: DESIGN_REMIX_STATUS.FAILED,
              error_message: errorMessage,
              process_uuid: postProcess.uuid,
            },
          });

          stopPolling();
        }
      }
      catch (error) {
        if (!cancelled) {
          setRemixError(error?.response?.data?.message || 'Unable to fetch page variant status');
        }
      }
    };

    checkStatus();
    intervalId = setInterval(checkStatus, DESIGN_REMIX_POLL_INTERVAL);

    return () => {
      stopPolling();
    };
  }, [remixProcessUuid, remixStatus, updateRecordRemix, isPublicView, dispatch]);

  useEffect(() => {
    if (!refiningProcessUuid || isPublicView) {
      return undefined;
    }

    let cancelled = false;
    let intervalId = null;

    const stopPolling = () => {
      cancelled = true;

      if (intervalId) {
        clearInterval(intervalId);
      }
    };

    const checkRefinementStatus = async () => {
      try {
        const postProcess = await getDesignReviewPostProcess(refiningProcessUuid);

        if (!postProcess || cancelled) {
          return;
        }

        if (postProcess.status === DESIGN_REMIX_STATUS.COMPLETED) {
          const resultData = postProcess.result_data || {};
          // Read the latest preview from the ref so the result never depends on updater timing
          const latestPreview = remixPreviewRef.current;
          const updatedRefinementsSnapshot = Array.isArray(latestPreview?.refinements)
            ? latestPreview.refinements.map(r => {
              if (r.process_uuid === refiningProcessUuid) {
                return {
                  ...r,
                  status: DESIGN_REMIX_STATUS.COMPLETED,
                  upload_info: resultData.upload_info,
                  html_upload_info: resultData.html_upload_info,
                  generated_at: resultData.generated_at,
                };
              }

              return r;
            })
            : [];

          if (latestPreview) {
            remixPreviewRef.current = { ...latestPreview, refinements: updatedRefinementsSnapshot };
            setRemixPreview(remixPreviewRef.current);
          }

          if (!cancelled) {
            setRefiningProcessUuid(null);
            setIsRefining(false);

            if (latestPreview) {
              updateRecordRemix({
                remix_preview: { refinements: updatedRefinementsSnapshot },
              });
            }

            dispatch(getReviewDataAction());
          }

          stopPolling();
        }
        else if (postProcess.status === DESIGN_REMIX_STATUS.FAILED) {
          const errorMessage = postProcess.error_message || 'Refinement failed';
          // Read the latest preview from the ref so the result never depends on updater timing
          const latestPreview = remixPreviewRef.current;
          const failedRefinementsSnapshot = Array.isArray(latestPreview?.refinements)
            ? latestPreview.refinements.map(r => {
              if (r.process_uuid === refiningProcessUuid) {
                return {
                  ...r,
                  status: DESIGN_REMIX_STATUS.FAILED,
                  error_message: errorMessage,
                };
              }

              return r;
            })
            : [];

          if (latestPreview) {
            remixPreviewRef.current = { ...latestPreview, refinements: failedRefinementsSnapshot };
            setRemixPreview(remixPreviewRef.current);
          }

          if (!cancelled) {
            setRemixError(errorMessage);
            setRefiningProcessUuid(null);
            setIsRefining(false);

            if (latestPreview) {
              updateRecordRemix({
                remix_preview: { refinements: failedRefinementsSnapshot },
              });
            }

            // Reset activeIndex to the last non-failed inspiration so the next
            // retry uses a healthy parent_process_uuid + html_url.
            const allInspirations = [
              { status: remixStatus, isOriginal: true },
              ...failedRefinementsSnapshot,
            ];
            let lastGoodIdx = 0;

            allInspirations.forEach((item, i) => {
              if (item.status !== DESIGN_REMIX_STATUS.FAILED) {
                lastGoodIdx = i;
              }
            });
            setActiveIndex(lastGoodIdx);
          }

          stopPolling();
        }
      }
      catch (error) {
        if (!cancelled) {
          setRemixError(error?.response?.data?.message || 'Unable to fetch refinement status');
        }
      }
    };

    checkRefinementStatus();
    intervalId = setInterval(checkRefinementStatus, DESIGN_REMIX_POLL_INTERVAL);

    return () => {
      stopPolling();
    };
  }, [refiningProcessUuid, isPublicView, updateRecordRemix, dispatch, remixStatus]);

  useEffect(() => {
    if (!remixPreview || isPublicView || refiningProcessUuid) {
      return;
    }

    const refinements = Array.isArray(remixPreview.refinements) ? remixPreview.refinements : [];
    const inProgressRefinement = refinements.find(
      r => r.status === DESIGN_REMIX_STATUS.IN_PROGRESS && r.process_uuid
    );

    if (!inProgressRefinement) {
      return;
    }

    // Poll every unfinished refinement (one at a time) so none is left stuck on "generating"
    setRefiningProcessUuid(inProgressRefinement.process_uuid);
    setIsRefining(true);

    const idx = refinements
      .filter(r => r.version >= 2)
      .sort((a, b) => a.version - b.version)
      .findIndex(r => r.process_uuid === inProgressRefinement.process_uuid);

    if (idx >= 0) {
      setActiveIndex(idx + 1); // +1 because original is at index 0
    }
  }, [remixPreview, isPublicView, refiningProcessUuid]);

  const handleGenerateRemix = useCallback(async () => {
    if (!isPageInRange) {
      setRemixError('Page is not available.');

      return;
    }

    if (!hasCredits) {
      setRemixError('You have no credits remaining. Please upgrade your plan.');

      return;
    }

    const figmaUserId = userData?.figma_user_id || userProfile?.figma_user_id;

    if (!figmaUserId || isTriggeringRemix || isTriggeringRef.current || !hasEligibleFeedback) {
      return;
    }

    isTriggeringRef.current = true;
    setIsTriggeringRemix(true);
    setRemixError('');
    setImageError('');

    try {
      const imageUrlForRemix = await ensureDesignImageUrl();

      if (!imageUrlForRemix) {
        throw new Error('Design image unavailable');
      }

      const feedbackText = eligibleFeedback;

      const response = await triggerPageRemix({
        imageUrl: imageUrlForRemix,
        page,
        feedbackText,
        figmaUserId,
        designReviewUuid,
      });

      setRemixProcessUuid(response.process_uuid);
      setRemixStatus(response.status ?? DESIGN_REMIX_STATUS.IN_PROGRESS);
      setRemixPreview(null);

      updateRecordRemix({
        status: response.status ?? DESIGN_REMIX_STATUS.IN_PROGRESS,
        process_uuid: response.process_uuid,
        upload_info: null,
        html_upload_info: null,
        generated_at: null,
        remix_preview: {
          status: DESIGN_REMIX_STATUS.IN_PROGRESS,
          process_uuid: response.process_uuid,
          html_upload_info: null,
        },
      });
    }
    catch (error) {
      const remixErrorMsg = error?.response?.data?.message
        || error?.message || 'Failed to start page variant. Please try again.';

      setRemixError(remixErrorMsg);
    }
    finally {
      isTriggeringRef.current = false;
      setIsTriggeringRemix(false);
    }
  }, [
    designReviewUuid,
    eligibleFeedback,
    ensureDesignImageUrl,
    hasCredits,
    hasEligibleFeedback,
    isPageInRange,
    isTriggeringRemix,
    page,
    updateRecordRemix,
    userData,
    userProfile,
  ]);

  const handleRefineRemix = useCallback(async refinementPrompt => {
    if (!isPageInRange) {
      setRemixError('Page is not available.');

      return;
    }

    if (isRefining || isRefiningRef.current || !activeInspiration) {
      return;
    }

    if (!hasCredits) {
      setRemixError('You have no credits remaining. Please upgrade your plan.');

      return;
    }

    const figmaUserId = userData?.figma_user_id || userProfile?.figma_user_id;

    if (!figmaUserId) {
      setRemixError('Figma account not connected. Please connect your Figma account.');

      return;
    }

    const effectivePrompt = refinementPrompt?.trim() || DEFAULT_REFINEMENT_PROMPT;

    isRefiningRef.current = true;
    setIsRefining(true);
    setRemixError('');
    setImageError('');

    try {
      const imageUrlForRemix = await ensureDesignImageUrl();

      if (!isMountedRef.current) {
        return;
      }

      if (!imageUrlForRemix) {
        throw new Error('Design image unavailable');
      }

      const htmlUploadKey = activeInspiration.html_upload_info?.key;
      const htmlUrl = htmlUploadKey
        ? `${process.env.REACT_APP_PREVIEW_CLOUDFRONT_URL}${htmlUploadKey}`
        : null;
      const maxVersion = inspirations.reduce(
        (max, item) => Math.max(max, item.version || 1),
        1
      );
      const nextVersion = maxVersion + 1;

      const response = await refineDesignRemix({
        designReviewUuid,
        insightUuid: pageKey,
        refinementPrompt: effectivePrompt,
        htmlUrl,
        imageUrl: imageUrlForRemix,
        parentProcessUuid: activeProcessUuid,
        version: nextVersion,
        insightText: eligibleFeedback,
        figmaUserId,
      });

      if (!isMountedRef.current) {
        return;
      }

      const newRefinement = {
        version: nextVersion,
        status: response.status ?? DESIGN_REMIX_STATUS.IN_PROGRESS,
        process_uuid: response.process_uuid,
        refinement_prompt: effectivePrompt,
        upload_info: null,
        html_upload_info: null,
      };

      // Another member may already have started this refinement — the backend then returns its process_uuid
      const existingRefinements = Array.isArray(remixPreview?.refinements) ? remixPreview.refinements : [];
      const isAlreadyListed = Boolean(response.process_uuid)
        && existingRefinements.some(r => r.process_uuid === response.process_uuid);
      const updatedRefinements = isAlreadyListed ? existingRefinements : [...existingRefinements, newRefinement];

      setRemixPreview(prev => {
        if (!prev) {
          return prev;
        }
        const existing = Array.isArray(prev.refinements) ? prev.refinements : [];

        if (response.process_uuid && existing.some(r => r.process_uuid === response.process_uuid)) {
          return prev;
        }

        return { ...prev, refinements: [...existing, newRefinement] };
      });

      setRefiningProcessUuid(response.process_uuid);

      updateRecordRemix({
        remix_preview: { refinements: updatedRefinements },
      });

      setActiveIndex(() => {
        const refinementIndex = updatedRefinements
          .filter(r => r.version >= 2)
          .sort((a, b) => a.version - b.version)
          .findIndex(r => r.process_uuid === response.process_uuid);

        // original(0) + position among refinements
        return refinementIndex >= 0 ? refinementIndex + 1 : updatedRefinements.length;
      });
    }
    catch (error) {
      if (!isMountedRef.current) {
        return;
      }

      const msg = error?.response?.data?.message
        || error?.message || 'Failed to start refinement. Please try again.';

      setRemixError(msg);
      setIsRefining(false);
    }
  }, [
    activeInspiration,
    activeProcessUuid,
    designReviewUuid,
    eligibleFeedback,
    ensureDesignImageUrl,
    hasCredits,
    inspirations,
    isPageInRange,
    isRefining,
    pageKey,
    remixPreview,
    updateRecordRemix,
    userData,
    userProfile,
  ]);

  const handleDismissVariant = useCallback(async (processUuid, isDismissed) => {
    if (!processUuid || !remixPreview || !canDismissVariants) {
      return;
    }

    setIsDismissingUuid(processUuid);

    try {
      await dismissVariant(processUuid, isDismissed);

      // Apply on the latest local state — a refinement may have finished while the request was in flight
      const latestPreview = remixPreviewRef.current;

      if (!latestPreview) {
        return;
      }

      let updatedPreview;

      if (latestPreview.process_uuid === processUuid) {
        updatedPreview = { ...latestPreview, is_dismissed: isDismissed };
      }
      else {
        const refinements = Array.isArray(latestPreview.refinements) ? latestPreview.refinements : [];

        updatedPreview = {
          ...latestPreview,
          refinements: refinements.map(
            r => (r.process_uuid === processUuid ? { ...r, is_dismissed: isDismissed } : r)
          ),
        };
      }

      remixPreviewRef.current = updatedPreview;
      setRemixPreview(updatedPreview);

      if (updatedPreview.process_uuid === processUuid) {
        updateRecordRemix({
          remix_preview: { is_dismissed: isDismissed },
        });
      }
      else {
        updateRecordRemix({
          remix_preview: { refinements: updatedPreview.refinements },
        });
      }
    }
    catch (error) {
      const message = error?.response?.data?.message
        || error?.response?.data?.detail
        || 'Failed to update variant';

      setRemixError(message);
      errorReactToastify(message, { autoClose: 5000, position: 'bottom-center', hideProgressBar: true });
    }
    finally {
      setIsDismissingUuid(null);
    }
  }, [canDismissVariants, remixPreview, updateRecordRemix]);

  const panels = useMemo(() => {
    // Idle state with eligible feedback → CTA panel (only for owners)
    if (isRemixIdle) {
      if (isPublicView || !isReviewOwner || !hasEligibleFeedback || !isPageInRange) {
        return [];
      }

      return [{
        key: `${pageKey}-cta`,
        variantIndex: 0,
        isOriginal: true,
        version: 0,
        status: null,
        page,
        insightUuid: pageKey,
        onGenerate: handleGenerateRemix,
        isGenerateBusy: isTriggeringRemix || isFetchingImage,
        isGenerateDisabled: !hasCredits || !hasEligibleFeedback,
        generateDisabledReason: !hasCredits ? 'Usage limit reached' : null,
      }];
    }

    if (isRemixInProgress && !remixPreview) {
      return [{
        key: `${pageKey}-v1`,
        variantIndex: 0,
        isOriginal: true,
        version: 1,
        status: DESIGN_REMIX_STATUS.IN_PROGRESS,
        previewSrc: null,
        htmlUrl: null,
        processUuid: remixProcessUuid,
        refinementPrompt: null,
        page,
        insightUuid: pageKey,
      }];
    }

    if (remixStatus === DESIGN_REMIX_STATUS.FAILED && !isPublicView) {
      return [{
        key: `${pageKey}-failed`,
        variantIndex: 0,
        isOriginal: true,
        version: 1,
        status: DESIGN_REMIX_STATUS.FAILED,
        page,
        insightUuid: pageKey,
        onGenerate: handleGenerateRemix,
        isGenerateBusy: isTriggeringRemix || isFetchingImage,
        isGenerateDisabled: !hasCredits || !hasEligibleFeedback,
        generateDisabledReason: !hasCredits ? 'Usage limit reached' : null,
        ctaTitle: 'Variant generation failed',
        ctaSubtitle: remixError || 'Please try again.',
        ctaButtonLabel: 'Try again',
      }];
    }

    if (!remixPreview) {
      return [];
    }

    const usedKeys = new Set();

    const unsorted = inspirations
      .filter(item => item.status !== DESIGN_REMIX_STATUS.FAILED)
      .map((item, idx) => {
        const uploadKey = item.upload_info?.key;
        const previewSrc = uploadKey
          ? `${process.env.REACT_APP_PREVIEW_CLOUDFRONT_URL}${uploadKey}`
          : null;
        const htmlKey = item.html_upload_info?.key;
        const htmlUrl = htmlKey
          ? `${process.env.REACT_APP_PREVIEW_CLOUDFRONT_URL}${htmlKey}`
          : null;

        const itemStatus = item.isOriginal
          ? (remixStatus ?? DESIGN_REMIX_STATUS.IN_PROGRESS)
          : item.status;

        return {
          key: buildUniquePanelKey(`${pageKey}-v${item.version}`, item.process_uuid, usedKeys),
          variantIndex: idx,
          isOriginal: Boolean(item.isOriginal),
          version: item.version,
          status: itemStatus,
          previewSrc,
          htmlUrl,
          processUuid: item.process_uuid ?? null,
          refinementPrompt: item.isOriginal ? null : (item.refinement_prompt ?? null),
          isDismissed: Boolean(item.is_dismissed),
          page,
          insightUuid: pageKey,
        };
      });

    return [
      ...unsorted.filter(p => !p.isDismissed),
      ...unsorted.filter(p => p.isDismissed),
    ];
  }, [
    isRemixIdle,
    isRemixInProgress,
    remixPreview,
    inspirations,
    remixStatus,
    remixProcessUuid,
    remixError,
    page,
    pageKey,
    handleGenerateRemix,
    isTriggeringRemix,
    isFetchingImage,
    hasCredits,
    hasEligibleFeedback,
    isPageInRange,
    isPublicView,
    isReviewOwner,
  ]);

  useEffect(() => {
    if (!isPageInRange) {
      clearPrototype(pageKey);

      return;
    }

    const panelsToRegister = isPublicView
      ? panels
        .filter(p => p.status === DESIGN_REMIX_STATUS.COMPLETED && !p.isDismissed)
        .map((p, i) => ({ ...p, variantIndex: i }))
      : panels;

    if (panelsToRegister.length === 0) {
      clearPrototype(pageKey);

      return;
    }

    const refinementsArr = Array.isArray(remixPreview?.refinements) ? remixPreview.refinements : [];
    const latestRefinement = refinementsArr.length ? refinementsArr[refinementsArr.length - 1] : null;
    const refinementError = !isPublicView && latestRefinement?.status === DESIGN_REMIX_STATUS.FAILED
      ? (latestRefinement.error_message || 'Refinement failed')
      : null;

    registerPrototype(pageKey, {
      page,
      status: remixStatus,
      previewSrc: activePreviewSrc,
      htmlUrl: activePreviewHtmlUrl,
      processUuid: activeProcessUuid,
      totalVariants: inspirations.length,
      activeVariantIndex: safeIndex,
      panels: panelsToRegister,
      isDismissingUuid,
      isOwnerView: !isPublicView && canDismissVariants,
      onDismissVariant: handleDismissVariant,
      onRefine: isPublicView || !isReviewOwner ? null : handleRefineRemix,
      isRefining,
      refineDisabledReason: hasCredits ? null : 'Usage limit reached',
      refinementError,
    });
  }, [
    panels,
    remixStatus,
    activePreviewSrc,
    activePreviewHtmlUrl,
    activeProcessUuid,
    inspirations.length,
    safeIndex,
    pageKey,
    page,
    isPublicView,
    isReviewOwner,
    canDismissVariants,
    isPageInRange,
    isDismissingUuid,
    handleDismissVariant,
    handleRefineRemix,
    isRefining,
    hasCredits,
    remixPreview,
    registerPrototype,
    clearPrototype,
  ]);

  useEffect(() => () => {
    clearPrototype(pageKey);
  }, [clearPrototype, pageKey]);

  // PageVariantPanel renders nothing in the DOM — it's a side-effect-only
  // component that polls, broadcasts state into PrototypePreviewContext, and
  // lets ImageCarousel render the carousel-side UI. Failures surface in the
  // carousel via the failed-CTA / refinement-error paths, not in the right rail.
  return null;
};

PageVariantPanel.propTypes = {
  page: PropTypes.number.isRequired,
  eligibleFeedback: PropTypes.array,
  designReviewUuid: PropTypes.string,
  isPublicView: PropTypes.bool,
};

PageVariantPanel.defaultProps = {
  eligibleFeedback: [],
  designReviewUuid: undefined,
  isPublicView: false,
};

export default PageVariantPanel;
